import { MigrationStatusResponse, MigrationStep } from 'types/migration';
import fetchWithRetry from './fetch-retry';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

async function fetchOrThrow(url: string, init: RequestInit): Promise<Response> {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) }
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res;
}

async function uploadFileOrThrow(url: string, formData: FormData): Promise<Response> {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res;
}

export const startMigration = async (locationId: number, resetSteps?: boolean): Promise<MigrationStatusResponse> => {
  let url = `/api/migration/start?locationId=${locationId}`;
  if (resetSteps) url += '&resetSteps=true';
  const res = await fetchOrThrow(url, { method: 'POST' });
  return res.json();
};

export const startNewImport = async (locationId: number): Promise<MigrationStatusResponse> => {
  const res = await fetchOrThrow(`/api/migration/start?locationId=${locationId}&resetSteps=true`, {
    method: 'POST'
  });
  return res.json();
};

export const getMigrationStatus = async (locationId: number): Promise<MigrationStatusResponse> =>
  await fetchWithRetry<MigrationStatusResponse>(
    `/api/migration/status?locationId=${locationId}`,
    { method: 'GET' },
    {} as MigrationStatusResponse
  );

export const downloadTemplate = (step: MigrationStep): void => {
  fetch(`/api/migration/templates/${step}`, {
    headers: authHeaders()
  })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${step.toLowerCase()}-template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });
};

export const uploadMigrationFile = async (
  locationId: number,
  step: MigrationStep,
  file: File
): Promise<MigrationStatusResponse> => {
  const formData = new FormData();
  formData.append('locationId', String(locationId));
  formData.append('file', file);

  const res = await uploadFileOrThrow(`/api/migration/${stepEndpoint(step)}`, formData);
  return res.json();
};

export const restartMigration = async (locationId: number, runId: number): Promise<void> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000);
  try {
    await fetchOrThrow(`/api/migration/restart?locationId=${locationId}&runId=${runId}`, { method: 'POST', signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getLocationsWithMigrationHistory = async (): Promise<number[]> => {
  const res = await fetchOrThrow('/api/migration/locations', { method: 'GET' });
  return res.json();
};

export const cancelMigration = async (locationId: number, runId: number): Promise<MigrationStatusResponse> => {
  const res = await fetchOrThrow(`/api/migration/cancel?locationId=${locationId}&runId=${runId}`, { method: 'POST' });
  return res.json();
};

function stepEndpoint(step: MigrationStep): string {
  switch (step) {
    case MigrationStep.CUSTOMER: return 'customers';
    case MigrationStep.TRUCKING_COMPANY: return 'trucking-companies';
    case MigrationStep.BOOKING: return 'bookings';
    case MigrationStep.ACTIVE_UNIT: return 'active-units';
    case MigrationStep.CONTRACT: return 'contracts';
    case MigrationStep.REDELIVERY: return 'redeliveries';
  }
}
